// quiz_management/TeacherDashboard.js
// Teacher dashboard for quiz creation, upload, and management

import React, { useState, useEffect } from 'react';
import API_BASE_URL from './config';
import './quizteacherdashboard.css';
import QuizQuestionLinker from './QuizQuestionLinker';

const TeacherDashboard = () => {
    const [activeTab, setActiveTab] = useState('upload'); // upload, manage, analytics
    const [quizzes, setQuizzes] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [topics, setTopics] = useState([]);
    const [subtopics, setSubtopics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [availableQuestions, setAvailableQuestions] = useState([]);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [questionSource, setQuestionSource] = useState('existing'); // 'existing' or 'excel'
    const [linkUploadProgress, setLinkUploadProgress] = useState(null);

    
    // Quiz creation form state
    const [quizForm, setQuizForm] = useState({
        quiz_title: '',
        quiz_description: '',
        subtopic_id: '',
        topic_id: '',
        time_limit_minutes: '',
        quiz_type: 'practice'
    });

    // Add hierarchy filter state for link questions
    const [linkFilters, setLinkFilters] = useState({
        selectedSubject: '',
        selectedChapter: '',
        selectedTopic: '',
        selectedSubtopic: '',
        chapters: [],
        topics: [],
        subtopics: []
    });

    // Excel upload state
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(null);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            // Load subjects for dropdowns
            const subjectsResponse = await fetch(`${API_BASE_URL}/subjects/`);
            const subjectsData = await subjectsResponse.json();
            setSubjects(subjectsData);

            // Load existing quizzes
            const quizzesResponse = await fetch(`${API_BASE_URL}/quiz/quiz/`);
            const quizzesData = await quizzesResponse.json();
            setQuizzes(quizzesData);
        } catch (err) {
            setError('Failed to load initial data');
            console.error('Error:', err);
        }
    };

    const loadChapters = async (subjectId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/quiz/report/chapters/subject/${subjectId}`);
            const data = await response.json();
            setChapters(data);
        } catch (err) {
            console.error('Error loading chapters:', err);
        }
    };

    const loadTopics = async (chapterId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/topics/?chapter_id=${chapterId}`);
            const data = await response.json();
            setTopics(data);
        } catch (err) {
            console.error('Error loading topics:', err);
        }
    };

    const loadSubtopics = async (topicId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/subtopics/?topic_id=${topicId}`);
            const data = await response.json();
            setSubtopics(data);
        } catch (err) {
            console.error('Error loading subtopics:', err);
        }
    };

    const handleQuizFormChange = (field, value) => {
        setQuizForm(prev => ({
            ...prev,
            [field]: value
        }));

        // Load dependent dropdowns
        if (field === 'subject_id') {
            loadChapters(value);
            setQuizForm(prev => ({ ...prev, chapter_id: '', topic_id: '', subtopic_id: '' }));
        } else if (field === 'chapter_id') {
            loadTopics(value);
            setQuizForm(prev => ({ ...prev, topic_id: '', subtopic_id: '' }));
        } else if (field === 'topic_id') {
            loadSubtopics(value);
            setQuizForm(prev => ({ ...prev, subtopic_id: '' }));
        }
    };

    const createQuiz = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/quiz/quiz/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...quizForm,
                    subtopic_id: parseInt(quizForm.subtopic_id),
                    topic_id: parseInt(quizForm.topic_id),
                    time_limit_minutes: quizForm.time_limit_minutes ? parseInt(quizForm.time_limit_minutes) : null
                })
            });

            if (response.ok) {
                const newQuiz = await response.json();
                setQuizzes(prev => [...prev, newQuiz]);
                setSuccess('Quiz created successfully!');
                setQuizForm({
                    quiz_title: '',
                    quiz_description: '',
                    subtopic_id: '',
                    topic_id: '',
                    time_limit_minutes: '',
                    quiz_type: 'practice'
                });
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to create quiz');
            }
        } catch (err) {
            setError('Network error. Please try again.');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    // In quizteacherdashboard.js, replace the handleFileUpload function with this:

const handleDownloadTemplate = async () => {
    if (!linkFilters.selectedSubject || !linkFilters.selectedChapter) {
        setError('Please select both Subject and Chapter first');
        return;
    }

    setLoading(true);
    setError(null);

    try {
        const response = await fetch(
            `${API_BASE_URL}/quiz-excel/download-template/?subject_id=${linkFilters.selectedSubject}&chapter_id=${linkFilters.selectedChapter}`
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to download template');
        }

        // Get filename from header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'quiz_questions_template.xlsx';
        
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }

        // Download file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSuccess('Template downloaded successfully! Fill the template and upload.');
    } catch (err) {
        setError(err.message || 'Failed to download template');
        console.error('Template download error:', err);
    } finally {
        setLoading(false);
    }
};

const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
        setError('Please select an Excel file');
        return;
    }

    if (!linkFilters.selectedSubject || !linkFilters.selectedChapter) {
        setError('Please select both Subject and Chapter');
        return;
    }

    if (loading) {
        return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('subject_id', linkFilters.selectedSubject);
    formData.append('chapter_id', linkFilters.selectedChapter);
    formData.append('generate_embeddings', 'true');

    try {
        const response = await fetch(`${API_BASE_URL}/quiz-excel/upload/`, {
            method: 'POST',
            body: formData
        });

        if (response.ok) {
            const result = await response.json();
            setUploadProgress(result);
            setSuccess(`Successfully uploaded ${result.successful_uploads} questions!`);
            setSelectedFile(null);
            
            // Reset file input
            const fileInput = document.querySelector('.file-input');
            if (fileInput) fileInput.value = '';
            
            // Refresh quiz list
            loadInitialData();
        } else {
            const errorData = await response.json();
            setError(errorData.detail || 'Failed to upload file');
        }
    } catch (err) {
        setError('Network error during upload. Please try again.');
        console.error('Upload error:', err);
    } finally {
        setLoading(false);
    }
};
    
    
    const loadAvailableQuestions = async (quizId) => {
        try {
            const quiz = quizzes.find(q => q.quiz_id === quizId);
            if (!quiz) return;

            // Load questions that match the quiz's subject/chapter/topic/subtopic
            const params = new URLSearchParams({
                subject_id: quiz.subject_id,
                chapter_id: quiz.chapter_id,
                topic_id: quiz.topic_id
            });
            if (quiz.subtopic_id) {
                params.append('subtopic_id', quiz.subtopic_id);
            }

            const response = await fetch(`${API_BASE_URL}/quiz/question/?${params}`);
            const data = await response.json();
            setAvailableQuestions(data);
        } catch (err) {
            console.error('Error loading questions:', err);
        }
    };

    const addQuestionToQuiz = async (quizId, questionId) => {
        try {
            // Get current question count for ordering
            const currentQuestionsResponse = await fetch(`${API_BASE_URL}/quiz/quiz/${quizId}/questions`);
            const questionsData = await currentQuestionsResponse.json();
            const nextOrder = questionsData.length + 1;

            const response = await fetch(`${API_BASE_URL}/quiz/quiz/${quizId}/add-question/${questionId}?question_order=${nextOrder}`, {
                method: 'POST'
            });

            if (response.ok) {
                setSuccess('Question added to quiz successfully!');
                // Refresh available questions and quiz data
                loadAvailableQuestions(quizId);
                loadInitialData();
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to add question to quiz');
            }
        } catch (err) {
            setError('Network error. Please try again.');
            console.error('Error:', err);
        }
    };
    
    // Computed filtered quizzes based on hierarchy
    const filteredQuizzes = quizzes.filter(quiz => {
        if (!linkFilters.selectedSubject) return false;
        if (quiz.subject_id != parseInt(linkFilters.selectedSubject)) return false;
        
        if (linkFilters.selectedChapter && quiz.chapter_id != parseInt(linkFilters.selectedChapter)) return false;
        if (linkFilters.selectedTopic && quiz.topic_id != parseInt(linkFilters.selectedTopic)) return false;
        if (linkFilters.selectedSubtopic && quiz.subtopic_id != parseInt(linkFilters.selectedSubtopic)) return false;

 
        return true;
    });

    // ADD THESE CONSOLE LOGS HERE
    
        
    // Handle hierarchy filter changes
    const handleLinkFilterChange = async (type, value) => {
        if (type === 'subject') {
            setLinkFilters({
                selectedSubject: value,
                selectedChapter: '',
                selectedTopic: '',
                selectedSubtopic: '',
                chapters: [],
                topics: [],
                subtopics: []
            });
            
            if (value) {
                try {
                    const response = await fetch(`${API_BASE_URL}/quiz/report/chapters/subject/${value}`);
                    const chaptersData = await response.json();
                    setLinkFilters(prev => ({
                        ...prev,
                        chapters: chaptersData
                    }));
                } catch (err) {
                    console.error('Error loading chapters:', err);
                }
            }
        } else if (type === 'chapter') {
            setLinkFilters(prev => ({
                ...prev,
                selectedChapter: value,
                selectedTopic: '',
                selectedSubtopic: '',
                topics: [],
                subtopics: []
            }));
            
            if (value) {
                try {
                    const response = await fetch(`${API_BASE_URL}/quiz/report/topics/chapter/${value}`);
                    const topicsData = await response.json();
                    setLinkFilters(prev => ({
                        ...prev,
                        topics: topicsData
                    }));
                } catch (err) {
                    console.error('Error loading topics:', err);
                }
            }
        } else if (type === 'topic') {
            setLinkFilters(prev => ({
                ...prev,
                selectedTopic: value,
                selectedSubtopic: '',
                subtopics: []
            }));
            
            if (value) {
                try {
                    const response = await fetch(`${API_BASE_URL}/quiz/report/subtopics/topic/${value}`);
                    const subtopicsData = await response.json();
                    setLinkFilters(prev => ({
                        ...prev,
                        subtopics: subtopicsData
                    }));
                } catch (err) {
                    console.error('Error loading subtopics:', err);
                }
            }
        } else if (type === 'subtopic') {
            setLinkFilters(prev => ({
                ...prev,
                selectedSubtopic: value
            }));
        }
    };
    
    const deleteQuiz = async (quizId) => {
        if (!window.confirm('Are you sure you want to delete this quiz?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/quiz/quiz/${quizId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                setQuizzes(prev => prev.filter(quiz => quiz.quiz_id !== quizId));
                setSuccess('Quiz deleted successfully');
            } else {
                setError('Failed to delete quiz');
            }
        } catch (err) {
            setError('Network error. Please try again.');
            console.error('Delete error:', err);
        }
    };


    // Updated Excel upload function to include hierarchy from filters
    const handleExcelUploadForQuiz = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            setError('Please select an Excel file');
            return;
        }

        if (!selectedQuiz) {
            setError('Please select a quiz first');
            return;
        }

        if (loading) {
            return; // Prevent double submission
        }

        setLoading(true);
        setError(null);
        setLinkUploadProgress(null);

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('generate_embeddings', 'true');
        formData.append('quiz_id', selectedQuiz);
        
        // Add hierarchy from filters
        if (linkFilters.selectedSubject) formData.append('subject_id', linkFilters.selectedSubject);
        if (linkFilters.selectedChapter) formData.append('chapter_id', linkFilters.selectedChapter);
        if (linkFilters.selectedTopic) formData.append('topic_id', linkFilters.selectedTopic);
        if (linkFilters.selectedSubtopic) formData.append('subtopic_id', linkFilters.selectedSubtopic);

        try {
            const response = await fetch(`${API_BASE_URL}/quiz/upload-excel-to-quiz/`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                setLinkUploadProgress(result);
                setSuccess(`Successfully uploaded ${result.successful_uploads} questions and added ${result.added_to_quiz} to quiz!`);
                setSelectedFile(null);
                
                // Reset file input
                const fileInput = document.querySelector('.file-input');
                if (fileInput) fileInput.value = '';
                
                // Refresh quiz list and available questions
                loadInitialData();
                if (selectedQuiz) {
                    loadAvailableQuestions(parseInt(selectedQuiz));
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to upload file');
            }
        } catch (err) {
            setError('Network error during upload. Please try again.');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Reset question source when quiz selection changes
    const handleQuizSelection = (quizId) => {
        setSelectedQuiz(quizId);
        setQuestionSource('existing'); // Reset to existing questions
        setLinkUploadProgress(null); // Clear any previous upload results
        if (quizId) {
            loadAvailableQuestions(parseInt(quizId));
        }
    };
    
    
    return (
        <div className="teacher-dashboard">
            <div className="dashboard-header">
                <h1>Teacher Quiz Dashboard</h1>
                <p>Create, upload, and manage quizzes</p>
            </div>

            {error && (
                <div className="alert alert-error">
                    {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    {success}
                    <button onClick={() => setSuccess(null)}>×</button>
                </div>
            )}

            <div className="dashboard-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
                    onClick={() => setActiveTab('upload')}
                >
                    📤 Upload Questions
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
                    onClick={() => setActiveTab('create')}
                >
                    ➕ Create Quiz
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'questions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('questions')}
                >
                    🔗 Link Questions
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'manage' ? 'active' : ''}`}
                    onClick={() => setActiveTab('manage')}
                >
                    📋 Manage Quizzes
                </button>
            </div>

            
            {activeTab === 'upload' && (
                <div className="upload-section">
                    <h2>Upload Questions from Excel</h2>
                    
                    {/* Simplified hierarchy filters - Only Subject and Chapter */}
                    <div className="filters-container">
                        <h3>Step 1: Select Subject and Chapter</h3>
                        <div className="filter-grid">
                            <div className="form-group">
                                <label>Subject:</label>
                                <select
                                    value={linkFilters.selectedSubject}
                                    onChange={(e) => handleLinkFilterChange('subject', e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Select Subject</option>
                                    {subjects.map(subject => (
                                        <option key={subject.subject_id} value={subject.subject_id}>
                                            {subject.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Chapter:</label>
                                <select
                                    value={linkFilters.selectedChapter}
                                    onChange={(e) => handleLinkFilterChange('chapter', e.target.value)}
                                    className="form-select"
                                    disabled={!linkFilters.selectedSubject}
                                >
                                    <option value="">Select Chapter</option>
                                    {linkFilters.chapters.map(chapter => (
                                        <option key={chapter.chapter_id} value={chapter.chapter_id}>
                                            {chapter.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        {/* Show selected hierarchy */}
                        {linkFilters.selectedSubject && linkFilters.selectedChapter && (
                            <div className="selected-hierarchy">
                                <strong>Selected:</strong>
                                <span> Subject: {subjects.find(s => s.subject_id == linkFilters.selectedSubject)?.name}</span>
                                <span>, Chapter: {linkFilters.chapters.find(c => c.chapter_id == linkFilters.selectedChapter)?.name}</span>
                            </div>
                        )}
                    </div>

                    {/* Download Template Section */}
                    {linkFilters.selectedSubject && linkFilters.selectedChapter && (
                        <div className="template-download-section">
                            <h3>Step 2: Download Excel Template</h3>
                            <p>Download the template with topic and subtopic dropdowns for this chapter</p>
                            <button 
                                className="download-template-btn"
                                onClick={handleDownloadTemplate}
                                disabled={loading}
                            >
                                {loading ? '⏳ Generating Template...' : '📥 Download Excel Template'}
                            </button>
                            <div className="upload-note">
                                <strong>Note:</strong> The template will contain dropdown lists for all topics and subtopics 
                                in <span>{linkFilters.chapters.find(c => c.chapter_id == linkFilters.selectedChapter)?.name}</span>.
                                Select topic and subtopic for each question from the dropdowns.
                            </div>
                        </div>
                    )}
                    
                    {/* Upload Section */}
                    {linkFilters.selectedSubject && linkFilters.selectedChapter && (
                        <>
                            <div className="upload-info">
                                <h3>Step 3: Fill Template and Upload</h3>
                                <ul>
                                    <li><strong>Required:</strong> Topic Name (dropdown), Subtopic Name (dropdown), Question Text, Option A, Option B, Correct Answer</li>
                                    <li><strong>Optional:</strong> Option C, Option D, Explanation, Difficulty, Question Type</li>
                                    <li><strong>Correct answer:</strong> Must be A, B, C, or D</li>
                                    <li><strong>Questions can have:</strong> 2, 3, or 4 options</li>
                                    <li><strong>Topic/Subtopic:</strong> Select from dropdown lists in Excel (typing will be rejected)</li>
                                </ul>
                            </div>

                            <form onSubmit={handleFileUpload} className="upload-form">
                                <div className="form-group">
                                    <label>Select Filled Excel File:</label>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={(e) => setSelectedFile(e.target.files[0])}
                                        className="file-input"
                                    />
                                    <small className="file-help">
                                        Upload the template after selecting topics/subtopics and filling question data.
                                    </small>
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={loading || !selectedFile} 
                                    className="upload-btn"
                                >
                                    {loading ? 'Processing...' : 'Upload Questions'}
                                </button>
                            </form>
                        </>
                    )}

                    {/* Show upload progress */}
                    {uploadProgress && (
                        <div className="upload-results">
                            <h4>Upload Results:</h4>
                            <div className="result-stats">
                                <div className="stat">
                                    <span className="stat-label">Total Questions:</span>
                                    <span className="stat-value">{uploadProgress.total_questions}</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-label">Successfully Uploaded:</span>
                                    <span className="stat-value success">{uploadProgress.successful_uploads}</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-label">Failed:</span>
                                    <span className="stat-value error">{uploadProgress.failed_uploads}</span>
                                </div>
                            </div>
                            
                            {uploadProgress.errors.length > 0 && (
                                <div className="upload-errors">
                                    <h5>Errors:</h5>
                                    <ul>
                                        {uploadProgress.errors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'create' && (
                <div className="create-section">
                    <h2>Create New Quiz</h2>
                    
                    <form onSubmit={createQuiz} className="quiz-form">
                        <div className="form-group">
                            <label>Quiz Title *:</label>
                            <input
                                type="text"
                                value={quizForm.quiz_title}
                                onChange={(e) => handleQuizFormChange('quiz_title', e.target.value)}
                                className="form-input"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Quiz Description:</label>
                            <textarea
                                value={quizForm.quiz_description}
                                onChange={(e) => handleQuizFormChange('quiz_description', e.target.value)}
                                className="form-textarea"
                                rows="3"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Subject *:</label>
                                <select
                                    value={quizForm.subject_id}
                                    onChange={(e) => handleQuizFormChange('subject_id', e.target.value)}
                                    className="form-select"
                                    required
                                >
                                    <option value="">Select Subject</option>
                                    {subjects.map(subject => (
                                        <option key={subject.subject_id} value={subject.subject_id}>
                                            {subject.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Chapter *:</label>
                                <select
                                    value={quizForm.chapter_id}
                                    onChange={(e) => handleQuizFormChange('chapter_id', e.target.value)}
                                    className="form-select"
                                    disabled={!quizForm.subject_id}
                                    required
                                >
                                    <option value="">Select Chapter</option>
                                    {chapters.map(chapter => (
                                        <option key={chapter.chapter_id} value={chapter.chapter_id}>
                                            {chapter.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Topic *:</label>
                                <select
                                    value={quizForm.topic_id}
                                    onChange={(e) => handleQuizFormChange('topic_id', e.target.value)}
                                    className="form-select"
                                    disabled={!quizForm.chapter_id}
                                    required
                                >
                                    <option value="">Select Topic</option>
                                    {topics.map(topic => (
                                        <option key={topic.topic_id} value={topic.topic_id}>
                                            {topic.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Subtopic *:</label>
                                <select
                                    value={quizForm.subtopic_id}
                                    onChange={(e) => handleQuizFormChange('subtopic_id', e.target.value)}
                                    className="form-select"
                                    disabled={!quizForm.topic_id}
                                    required
                                >
                                    <option value="">Select Subtopic</option>
                                    {subtopics.map(subtopic => (
                                        <option key={subtopic.subtopic_id} value={subtopic.subtopic_id}>
                                            {subtopic.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Time Limit (minutes):</label>
                                <input
                                    type="number"
                                    value={quizForm.time_limit_minutes}
                                    onChange={(e) => handleQuizFormChange('time_limit_minutes', e.target.value)}
                                    className="form-input"
                                    placeholder="Leave empty for no limit"
                                />
                            </div>

                            <div className="form-group">
                                <label>Quiz Type:</label>
                                <select
                                    value={quizForm.quiz_type}
                                    onChange={(e) => handleQuizFormChange('quiz_type', e.target.value)}
                                    className="form-select"
                                >
                                    <option value="practice">Practice</option>
                                    <option value="assessment">Assessment</option>
                                    <option value="review">Review</option>
                                </select>
                            </div>
                        </div>

                        <button type="submit" disabled={loading} className="create-btn">
                            {loading ? 'Creating...' : 'Create Quiz'}
                        </button>
                    </form>
                </div>
            )}

            
            

            {activeTab === 'questions' && (
    <div className="questions-section">
        <h2>Link Questions to Quiz</h2>
        
        {/* KEEP: Hierarchy Filters for Quiz Selection */}
        <div className="hierarchy-filters">
            <h3>Filter Quizzes by Hierarchy</h3>
            <div className="filter-row">
                <div className="filter-group">
                    <label htmlFor="link-subject-select">Subject</label>
                    <select
                        id="link-subject-select"
                        value={linkFilters.selectedSubject}
                        onChange={(e) => handleLinkFilterChange('subject', e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Subjects</option>
                        {subjects.map(subject => (
                            <option key={subject.subject_id} value={subject.subject_id}>
                                {subject.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="link-chapter-select">Chapter</label>
                    <select
                        id="link-chapter-select"
                        value={linkFilters.selectedChapter}
                        onChange={(e) => handleLinkFilterChange('chapter', e.target.value)}
                        className="filter-select"
                        disabled={!linkFilters.selectedSubject}
                    >
                        <option value="">All Chapters</option>
                        {linkFilters.chapters.map(chapter => (
                            <option key={chapter.chapter_id} value={chapter.chapter_id}>
                                {chapter.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="link-topic-select">Topic</label>
                    <select
                        id="link-topic-select"
                        value={linkFilters.selectedTopic}
                        onChange={(e) => handleLinkFilterChange('topic', e.target.value)}
                        className="filter-select"
                        disabled={!linkFilters.selectedChapter}
                    >
                        <option value="">All Topics</option>
                        {linkFilters.topics.map(topic => (
                            <option key={topic.topic_id} value={topic.topic_id}>
                                {topic.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="link-subtopic-select">Subtopic</label>
                    <select
                        id="link-subtopic-select"
                        value={linkFilters.selectedSubtopic}
                        onChange={(e) => handleLinkFilterChange('subtopic', e.target.value)}
                        className="filter-select"
                        disabled={!linkFilters.selectedTopic}
                    >
                        <option value="">All Subtopics</option>
                        {linkFilters.subtopics.map(subtopic => (
                            <option key={subtopic.subtopic_id} value={subtopic.subtopic_id}>
                                {subtopic.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
        
        {/* KEEP: Quiz Selector */}
        <div className="quiz-selector">
            <label>Select Quiz to Add Questions:</label>
            <select
                value={selectedQuiz || ''}
                onChange={(e) => handleQuizSelection(e.target.value)}
                className="form-select"
            >
                <option value="">Choose a quiz...</option>
                {filteredQuizzes.map(quiz => (
                    <option key={quiz.quiz_id} value={quiz.quiz_id}>
                        {quiz.quiz_title} ({quiz.total_questions} questions)
                    </option>
                ))}
            </select>
            <small className="quiz-filter-info">
                {filteredQuizzes.length} of {quizzes.length} quizzes shown
                {linkFilters.selectedSubject && ` (filtered by hierarchy)`}
            </small>
        </div>

        {/* KEEP: Question Source Selector */}
        {selectedQuiz && (
            <div className="question-source-selector">
                <h3>Choose Question Source</h3>
                <div className="source-options">
                    <label className="source-option">
                        <input
                            type="radio"
                            name="questionSource"
                            value="existing"
                            checked={questionSource === 'existing'}
                            onChange={(e) => setQuestionSource(e.target.value)}
                        />
                        <span className="option-content">
                            <strong>📚 Existing Questions</strong>
                            <small>Select from already uploaded questions</small>
                        </span>
                    </label>
                    
                    <label className="source-option">
                        <input
                            type="radio"
                            name="questionSource"
                            value="excel"
                            checked={questionSource === 'excel'}
                            onChange={(e) => setQuestionSource(e.target.value)}
                        />
                        <span className="option-content">
                            <strong>📤 Excel Upload</strong>
                            <small>Upload new questions from Excel file</small>
                        </span>
                    </label>
                </div>
            </div>
        )}

        {/* NEW: Replace old existing questions section with QuizQuestionLinker */}
        {selectedQuiz && questionSource === 'existing' && (
            <QuizQuestionLinker 
                selectedQuiz={quizzes.find(q => q.quiz_id == selectedQuiz)}
                onQuestionsAdded={() => {
                    loadInitialData(); // Refresh quiz list
                    setSuccess('Questions added successfully!');
                }}
            />
        )}

        {/* KEEP: Excel Upload Section */}
        {selectedQuiz && questionSource === 'excel' && (
            <div className="excel-upload-section">
                <h3>Upload Questions from Excel</h3>
                
                <div className="upload-info">
                    <h4>Excel Format Requirements:</h4>
                    <ul>
                        <li><strong>Required columns:</strong> question_text, optionA, optionB, correct_answer</li>
                        <li><strong>Optional columns:</strong> optionC, optionD, explanation, difficulty_level, question_type</li>
                        <li><strong>Correct answer:</strong> Must be A, B, C, or D</li>
                        <li><strong>Questions can have:</strong> 2, 3, or 4 options</li>
                        <li><strong>Hierarchy IDs:</strong> Automatically taken from selected filters above</li>
                    </ul>
                    <div className="upload-note">
                        <strong>Note:</strong> Questions will use the hierarchy selected above: 
                        {linkFilters.selectedSubject && <span> Subject: {subjects.find(s => s.subject_id == linkFilters.selectedSubject)?.name}</span>}
                        {linkFilters.selectedChapter && <span>, Chapter: {linkFilters.chapters.find(c => c.chapter_id == linkFilters.selectedChapter)?.name}</span>}
                        {linkFilters.selectedTopic && <span>, Topic: {linkFilters.topics.find(t => t.topic_id == linkFilters.selectedTopic)?.name}</span>}
                        {linkFilters.selectedSubtopic && <span>, Subtopic: {linkFilters.subtopics.find(st => st.subtopic_id == linkFilters.selectedSubtopic)?.name}</span>}
                    </div>
                </div>

                <form onSubmit={handleExcelUploadForQuiz} className="upload-form">
                    <div className="form-group">
                        <label>Select Excel File:</label>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                            className="file-input"
                        />
                        <small className="file-help">
                            Excel file will be processed and questions added to: <strong>{quizzes.find(q => q.quiz_id == selectedQuiz)?.quiz_title}</strong>
                        </small>
                    </div>

                    <button type="submit" disabled={loading || !selectedFile} className="upload-btn">
                        {loading ? 'Processing...' : 'Upload & Add to Quiz'}
                    </button>
                </form>

                {linkUploadProgress && (
                    <div className="upload-results">
                        <h4>Upload Results:</h4>
                        <div className="result-stats">
                            <div className="stat">
                                <span className="stat-label">Total Questions:</span>
                                <span className="stat-value">{linkUploadProgress.total_questions}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Successfully Added:</span>
                                <span className="stat-value success">{linkUploadProgress.successful_uploads}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Failed:</span>
                                <span className="stat-value error">{linkUploadProgress.failed_uploads}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Added to Quiz:</span>
                                <span className="stat-value success">{linkUploadProgress.added_to_quiz}</span>
                            </div>
                        </div>
                        
                        {linkUploadProgress.errors.length > 0 && (
                            <div className="upload-errors">
                                <h5>Errors:</h5>
                                <ul>
                                    {linkUploadProgress.errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )}
    </div>
)}
            {activeTab === 'manage' && (
                <div className="manage-section">
                    <h2>Manage Existing Quizzes</h2>
                    
                    <div className="quiz-list">
                        {quizzes.length === 0 ? (
                            <div className="no-quizzes">
                                <p>No quizzes created yet. Create your first quiz!</p>
                            </div>
                        ) : (
                            quizzes.map(quiz => (
                                <div key={quiz.quiz_id} className="quiz-item">
                                    <div className="quiz-info">
                                        <h3>{quiz.quiz_title}</h3>
                                        <p>{quiz.quiz_description || 'No description'}</p>
                                        <div className="quiz-meta">
                                            <span>Questions: {quiz.total_questions}</span>
                                            <span>Type: {quiz.quiz_type}</span>
                                            <span>Status: {quiz.is_active ? 'Active' : 'Inactive'}</span>
                                        </div>
                                    </div>
                                    <div className="quiz-actions">
                                        <button 
                                            className="action-btn edit"
                                            onClick={() => console.log('Edit quiz', quiz.quiz_id)}
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            className="action-btn view"
                                            onClick={() => console.log('View analytics', quiz.quiz_id)}
                                        >
                                            Analytics
                                        </button>
                                        <button 
                                            className="action-btn delete"
                                            onClick={() => deleteQuiz(quiz.quiz_id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeacherDashboard;